/**
 * Reporting chart kit — pure model (no React, no Recharts). Charts only ever plot figures that came from the API;
 * these helpers turn them into plot rows, pick the token colour for a meaning, format axis ticks and tooltip values,
 * and describe the chart for screen readers.
 *
 * Money stays a decimal string everywhere except the plotted number (`toPlotNumber`, shared/lib/money): tooltips and
 * the data table render the original string in full (AED, two decimals); axis ticks may be compact (1.2M).
 */
import { formatInteger, formatPercent } from '@/shared/lib/format';
import { formatMoney, formatPlotTick, isDecimalString, toPlotNumber } from '@/shared/lib/money';

/** What a mark means. Colour follows meaning (tokens.css "Chart marks"), never the series position. */
export type ChartTone = 'progress' | 'approved' | 'positive' | 'positiveStrong' | 'attentionSoft' | 'attention' | 'negative' | 'neutral' | 'neutralSoft';
export type ChartValueKind = 'money' | 'count' | 'percent' | 'days';
export type MarkerShape = 'circle' | 'square' | 'triangle' | 'diamond' | 'cross';

export interface ChartSeries {
  key: string;
  label: string;
  tone: ChartTone;
  /** Legend / point marker; defaults by position so identity never relies on colour alone. */
  marker?: MarkerShape;
}

/** A figure as the API sent it: a decimal string (money, rates, days), an integer, or missing. */
export type ChartValue = string | number | null | undefined;

export interface ChartDatum {
  /** Stable id (bank code, period, product code…) — used for drill-through. */
  key: string;
  /** Display label (already translated / formatted). */
  label: string;
  values: Record<string, ChartValue>;
  /** Optional target for the "bar with target marker" form (same kind as the values). */
  target?: ChartValue;
}

export const TONE_TOKEN: Record<ChartTone, string> = {
  progress: 'var(--color-chart-progress)',
  approved: 'var(--color-chart-approved)',
  positive: 'var(--color-chart-positive)',
  positiveStrong: 'var(--color-chart-positive-strong)',
  attentionSoft: 'var(--color-chart-attention-soft)',
  attention: 'var(--color-chart-attention)',
  negative: 'var(--color-chart-negative)',
  neutral: 'var(--color-chart-neutral)',
  neutralSoft: 'var(--color-chart-neutral-soft)',
};

const MARKERS: readonly MarkerShape[] = ['circle', 'square', 'triangle', 'diamond', 'cross'];
/** Line dash per series position (solid, dashed, dotted, dash-dot) — the second, non-colour channel of a trend. */
const DASHES: readonly (string | undefined)[] = [undefined, '6 3', '2 3', '8 3 2 3'];

export interface SeriesStyle {
  key: string;
  label: string;
  color: string;
  marker: MarkerShape;
  dash: string | undefined;
}

export function seriesStyles(series: readonly ChartSeries[]): SeriesStyle[] {
  return series.map((s, i) => ({ key: s.key, label: s.label, color: TONE_TOKEN[s.tone], marker: s.marker ?? MARKERS[i % MARKERS.length] ?? 'circle', dash: DASHES[i % DASHES.length] }));
}

/** A figure as a plot number (null = no mark). Money and decimal strings go through shared/lib/money. */
export function plotValue(v: ChartValue): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  return toPlotNumber(v);
}

export const TARGET_KEY = '__target';
export const INDEX_KEY = '__index';
export const LABEL_KEY = '__label';

export type PlotRow = Record<string, number | string | null>;

/** Data → rows Recharts can plot (`__label`, `__index`, one number per series, optional `__target`). */
export function plotRows(data: readonly ChartDatum[], series: readonly ChartSeries[]): PlotRow[] {
  return data.map((d, i) => {
    const row: PlotRow = { [LABEL_KEY]: d.label, [INDEX_KEY]: i };
    for (const s of series) row[s.key] = plotValue(d.values[s.key]);
    if (d.target !== undefined) row[TARGET_KEY] = plotValue(d.target);
    return row;
  });
}

/** True when nothing would be drawn (no figure or every figure zero). */
export function isBlank(data: readonly ChartDatum[], series: readonly ChartSeries[]): boolean {
  return !data.some((d) => series.some((s) => {
    const v = plotValue(d.values[s.key]);
    return v !== null && v !== 0;
  }));
}

/** Full value for tooltips and the data table (money: two decimals, never abbreviated). */
export function formatChartValue(v: ChartValue, kind: ChartValueKind): string {
  if (v === null || v === undefined || v === '') return '—';
  switch (kind) {
    case 'money':
      return typeof v === 'string' && isDecimalString(v) ? formatMoney(v) : String(v);
    case 'percent':
      return typeof v === 'string' ? formatPercent(v, 1) : formatPercent(String(v), 1);
    case 'days':
      return String(v);
    default:
      return formatInteger(typeof v === 'number' ? v : (plotValue(v) ?? 0));
  }
}

/** Axis tick: compact for money (1.2M), whole numbers otherwise; Western digits always. */
export function formatAxisTick(n: number, kind: ChartValueKind): string {
  if (kind === 'money') return formatPlotTick(n);
  if (kind === 'percent') return `${formatInteger(n)}%`;
  return formatInteger(n);
}

/**
 * Axis placement for the reading direction. In RTL the category axis runs from the inline start (right), so time and
 * category order read the same way as the text, and the value axis sits on the inline start.
 */
export function axisLayout(rtl: boolean, orientation: 'columns' | 'bars') {
  // Recharts names axis sides physically; this is the one place that maps the reading direction onto them.
  // eslint-disable-next-line no-restricted-syntax -- Recharts API value, derived from the direction, not a CSS utility
  const side = rtl ? ('right' as const) : ('left' as const);
  return orientation === 'columns' ? { categoryReversed: rtl, valueReversed: false, valueAxisSide: side } : { categoryReversed: false, valueReversed: rtl, valueAxisSide: side };
}

/** The datum with the largest value of a series (for the screen-reader summary). */
export function peak(data: readonly ChartDatum[], seriesKey: string): ChartDatum | null {
  let best: ChartDatum | null = null;
  let bestValue = -Infinity;
  for (const d of data) {
    const v = plotValue(d.values[seriesKey]);
    if (v !== null && v > bestValue) {
      best = d;
      bestValue = v;
    }
  }
  return best;
}

/** Donuts are only for a part-to-whole of at most five categories. */
export const DONUT_MAX = 5;
export const donutAllowed = (data: readonly ChartDatum[]) => data.length > 0 && data.length <= DONUT_MAX;
